
import React, { ReactElement } from 'react';
import { ChartContainer } from '@/components/ui/chart';
import { useIsMobile } from '@/hooks/use-mobile';

interface ResponsiveChartContainerProps {
  config: any;
  children: React.ReactElement;
  className?: string;
  mobileHeight?: number;
  desktopHeight?: number;
  mobilePadding?: string;
  desktopPadding?: string;
}

const ResponsiveChartContainer: React.FC<ResponsiveChartContainerProps> = ({ 
  config, 
  children, 
  className = "",
  mobileHeight = 220,
  desktopHeight = 300,
  mobilePadding = "px-0",
  desktopPadding = "px-4"
}) => {
  const { isMobile, screenSize } = useIsMobile();
  
  // Add responsive configurations with fine-tuned settings for different mobile sizes
  const mobileConfig = isMobile ? {
    ...config,
    // Adjust margins for mobile based on screen size
    margin: config.margin || (
      screenSize === 'xs' 
        ? { top: 10, right: 5, left: 0, bottom: 15 } 
        : { top: 15, right: 10, left: 0, bottom: 20 }
    ),
    // Reduce font size for mobile
    fontSize: screenSize === 'xs' ? 10 : (config.fontSize || 12),
    // Make sure chart adapts to container width
    width: "100%",
    // Ensure we have reasonable defaults to prevent rendering issues
    padding: config.padding || { right: 10 },
    // Improve mobile touch interactions
    activeDot: { r: 6, strokeWidth: 2 },
    dot: { r: 3 },
  } : config;
  
  // Calculate height based on screen size
  const height = isMobile 
    ? (screenSize === 'xs' ? mobileHeight * 0.9 : mobileHeight) 
    : desktopHeight;
  
  return (
    <div 
      className={`${className} w-full ${isMobile ? mobilePadding : desktopPadding} transition-all duration-300 rounded-lg hover:shadow-sm`} 
      style={{ height }}
    >
      <div className="w-full h-full animate-fadeIn">
        <ChartContainer
          config={mobileConfig}
          className="w-full h-full"
        >
          {children}
        </ChartContainer>
      </div>
    </div>
  );
};

export default ResponsiveChartContainer;
